import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    useTheme,
    Paper,
    alpha,
    Slide,
    DialogContentText,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import constantes from '../../config/constantes';
import { useMain } from '../../contexts/MainContext';
import AudioVisualizer from '../AudioVisualizer';
import { gerarArquivoDaGravacao } from '../../helpers/Mic';
import { sampleRates } from '../../services/useMicV2';
import { transcribeLemonfoxOnly } from '../../services/audio'; // Importar a nova função
import { WarningAmber } from '@mui/icons-material';

const RECORDING_TIME_LIMIT_SECONDS = 60; // Limite de tempo em segundos

const textos = [
    'Hoje eu acordei um pouco mais cedo do que o normal. Preparei um café forte, abri a janela e deixei o ar fresco entrar. Gosto desses momentos calmos antes que o dia comece de verdade.',
    'Estava caminhando pela rua quando vi um cachorro brincando com uma folha seca. Parei por um instante, observei e percebi como às vezes a gente esquece de reparar nas pequenas coisas.',
    'Algumas pessoas preferem o silêncio, outras se sentem melhor com música ao fundo. Eu acho que tudo depende do momento e do tipo de energia que a gente precisa naquele dia.',
    'Ontem à noite caiu uma chuva fina que durou horas. Fiquei ouvindo o som das gotas batendo na janela enquanto lia um livro. Foi uma das noites mais tranquilas da semana.',
];

const qtdTentativasObrigatorias = 3; // Número de tentativas obrigatórias para registrar a marca vocal

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const PopupMarcaVocal = ({ open, onClose, onOpen }) => {
    const theme = useTheme();
    const [isRecording, setIsRecording] = useState(false);
    const { usuario } = useMain();
    const [recordingComplete, setRecordingComplete] = useState(false);
    const [message, setMessage] = useState(
        'Para otimizar a precisão das transcrições e o reconhecimento de sua voz, por favor, grave uma amostra vocal. Isso nos ajuda a entender melhor suas particularidades vocais, como tom e sotaque, tornando os resumos e análises de suas falas mais exatos e personalizados.',
    );
    const [tentativas, setTentativas] = useState(0); // Estado para controlar o número de tentativas
    const [audioStream, setAudioStream] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [audioContext, setAudioContext] = useState(null);
    const [audioInput, setAudioInput] = useState(null);
    const [recorder, setRecorder] = useState(null);
    const recorderInfos = useRef({ leftchannel: [], rightchannel: [], recordingLength: 0 });
    const bufferSize = 2048; // igual ao mic.js
    const sampleRate = sampleRates.ideal; // igual ao mic.js
    const [textoParaLeitura] = useState(textos[Math.floor(Math.random() * textos.length)]);
    const recordingTimeoutRef = useRef(null); // Ref para o timeout
    const [elapsedTime, setElapsedTime] = useState(0); // Estado para o tempo decorrido
    const elapsedTimeIntervalRef = useRef(null); // Ref para o intervalo do tempo decorrido
    const [alertCancelarMarcaVocal, setAlertCancelarMarcaVocal] = useState(false);

    // Função para formatar o tempo em MM:SS
    const formatTime = (timeInSeconds) => {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = timeInSeconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    // Função para normalizar o texto
    const normalizeText = (text) => {
        return text
            .toLowerCase()
            .replace(/[^\w\s]/g, '') // Remove todos os caracteres especiais exceto letras, números e espaços
            .replace(/\s+/g, ' ') // Substitui múltiplos espaços por um único espaço
            .trim(); // Remove espaços do início e fim
    };

    // Função para calcular a Similaridade de Jaccard
    const calculateJaccardSimilarity = (s1, s2) => {
        const normalizedS1 = normalizeText(s1);
        const normalizedS2 = normalizeText(s2);

        const set1 = new Set(normalizedS1.split(/\s+/)); // Divide em palavras e converte para minúsculas
        const set2 = new Set(normalizedS2.split(/\s+/)); // Divide em palavras e converte para minúsculas

        const intersection = new Set([...set1].filter((word) => set2.has(word)));
        const union = new Set([...set1, ...set2]);

        if (union.size === 0) {
            return 1.0; // Ambas as strings estão vazias ou contêm apenas espaços
        }

        return intersection.size / union.size;
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            setAudioStream(stream);
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            const context = new AudioContextClass({ sampleRate });
            setAudioContext(context);
            const input = context.createMediaStreamSource(stream);
            setAudioInput(input);
            const scriptProcessor = context.createScriptProcessor(bufferSize, 2, 2);
            setRecorder(scriptProcessor);
            recorderInfos.current = { leftchannel: [], rightchannel: [], recordingLength: 0 };

            input.connect(scriptProcessor);
            scriptProcessor.connect(context.destination);

            scriptProcessor.onaudioprocess = function (e) {
                recorderInfos.current.leftchannel.push(new Float32Array(e.inputBuffer.getChannelData(0)));
                recorderInfos.current.rightchannel.push(new Float32Array(e.inputBuffer.getChannelData(1)));
                recorderInfos.current.recordingLength += bufferSize;
            };

            setIsRecording(true);
            setRecordingComplete(false); // Reseta o estado de gravação completa
            setMessage('Gravando sua marca vocal...');
            setElapsedTime(0); // Reseta o tempo decorrido

            // Inicia o contador de tempo decorrido
            if (elapsedTimeIntervalRef.current) {
                clearInterval(elapsedTimeIntervalRef.current);
            }
            elapsedTimeIntervalRef.current = setInterval(() => {
                setElapsedTime((prevTime) => prevTime + 1);
            }, 1000);

            // Limite de tempo
            if (recordingTimeoutRef.current) {
                clearTimeout(recordingTimeoutRef.current);
            }
            recordingTimeoutRef.current = setTimeout(() => {
                // Notificação de tempo esgotado
                if (typeof Notification !== 'undefined') {
                    // Checar se Notification API está disponível
                    const showNotification = () => {
                        new Notification('Tempo Esgotado!', {
                            body: `A gravação da sua marca vocal atingiu o limite de ${RECORDING_TIME_LIMIT_SECONDS} segundos e foi interrompida.`,
                            // icon: 'path/to/your/icon.png' // Opcional: adicione um caminho para um ícone
                        });
                    };

                    if (Notification.permission === 'granted') {
                        showNotification();
                    } else if (Notification.permission !== 'denied') {
                        Notification.requestPermission().then((permission) => {
                            if (permission === 'granted') {
                                showNotification();
                            }
                        });
                    }
                }
                // Continua o fluxo normal, parando a gravação
                stopRecording();
            }, RECORDING_TIME_LIMIT_SECONDS * 1000);
        } catch (error) {
            console.error('Erro ao acessar o microfone:', error);
            setMessage('Erro ao acessar o microfone. Verifique as permissões do seu navegador.');
        }
    };

    const stopRecording = async () => {
        if (recordingTimeoutRef.current) {
            clearTimeout(recordingTimeoutRef.current); // Limpa o timeout de limite de gravação
        }
        if (elapsedTimeIntervalRef.current) {
            clearInterval(elapsedTimeIntervalRef.current); // Limpa o intervalo do tempo decorrido
        }

        if (recorder && audioContext) {
            recorder.disconnect();
            audioInput && audioInput.disconnect();
            audioContext && audioContext.close();
        }
        if (audioStream) {
            audioStream.getTracks().forEach((track) => track.stop());
        }
        setIsRecording(false);
        setIsProcessing(true);
        setMessage('Processando áudio e verificando transcrição...');

        try {
            const { leftchannel, rightchannel, recordingLength } = recorderInfos.current;
            const blob = await gerarArquivoDaGravacao(leftchannel, rightchannel, recordingLength, sampleRate);

            // Gerar um nome único para o arquivo (hash simples baseado no timestamp)
            const fileName = `marca_vocal_${Date.now().toString(36)}.wav`;

            // Enviar para a API de transcrição
            const transcriptionResult = await transcribeLemonfoxOnly(blob, fileName);

            if (transcriptionResult && typeof transcriptionResult === 'string') {
                const similarity = calculateJaccardSimilarity(transcriptionResult, textoParaLeitura);
                if (similarity >= 0.7) {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = () => {
                        const base64data = reader.result;
                        const marcaVocalData = {
                            audioBase64: base64data,
                            dataGravacao: new Date().toISOString(),
                        };
                        localStorage.setItem(`${constantes.audioMarcaVocal}${usuario.id}`, JSON.stringify(marcaVocalData)); // Alterado para localStorage
                        const dataAmanha = new Date();
                        dataAmanha.setDate(dataAmanha.getDate() + 1);
                        localStorage.setItem(`${constantes.audioMarcaVocal}${usuario.id}-dataCriacao`, dataAmanha.toISOString()); // Alterado para localStorage
                        setMessage('Marca vocal registrada com sucesso! A transcrição foi verificada.');
                        setRecordingComplete(true);
                        setIsProcessing(false);
                        // Fechar o popup automaticamente após sucesso
                        // onClose(); // Removido para permitir que o usuário clique em Concluir
                    };
                } else {
                    setMessage('Sua gravação não foi clara o suficiente. Por favor, tente gravar novamente.');
                    console.log('Similaridade não atingida:', similarity);
                    console.log('Texto transcrito:', transcriptionResult);
                    console.log('Texto original:', textoParaLeitura);
                    localStorage.removeItem(`${constantes.audioMarcaVocal}${usuario.id}`); // Apaga a marca vocal do sessionSotrage
                    localStorage.removeItem(`${constantes.audioMarcaVocal}${usuario.id}-dataCriacao`); // Apaga a data de criação do sessionSotrage

                    setRecordingComplete(false); // Permite gravar novamente
                    setIsProcessing(false);
                }
            } else {
                throw new Error('Falha ao obter a transcrição do áudio.');
            }
        } catch (error) {
            console.error('Erro ao processar o áudio ou verificar transcrição:', error);
            setMessage('Erro ao processar o áudio ou verificar a transcrição. Tente novamente.');
            setRecordingComplete(false); // Permite gravar novamente
            setIsProcessing(false);
        }
        setAudioStream(null);
        setAudioContext(null);
        setAudioInput(null);
        setRecorder(null);
        setTentativas((old) => old + 1); // Incrementa o número de tentativas
    };

    const handleClose = () => {
        console.log('handleClose chamado');
        if (recordingComplete) {
            if (isRecording) {
                stopRecording(); // Garante que a gravação pare se estiver ativa
            }
            resetRecordingOnly();
            onClose();
        }
    };

    const handleGiveUp = () => {
        console.log('handleGiveUp chamado');
        setAlertCancelarMarcaVocal(false);
        resetRecordingOnly();
        onClose();
    };

    const resetRecording = () => {
        resetRecordingOnly();
        // Não inicia a gravação automaticamente, espera o clique do usuário
        // setTimeout(() => {
        // startRecording();
        // }, 500);
    };

    const resetRecordingOnly = () => {
        setTentativas(0);
        setIsRecording(false);
        setRecordingComplete(false);
        setMessage(
            'Para otimizar a precisão das transcrições e o reconhecimento de sua voz, por favor, grave uma amostra vocal. Isso nos ajuda a entender melhor suas particularidades vocais, como tom e sotaque, tornando os resumos e análises de suas falas mais exatos e personalizados.',
        );
        setAudioStream(null);
        setAudioContext(null);
        setAudioInput(null);
        setRecorder(null);
        recorderInfos.current = { leftchannel: [], rightchannel: [], recordingLength: 0 };
        if (recordingTimeoutRef.current) {
            clearTimeout(recordingTimeoutRef.current);
        }
        if (elapsedTimeIntervalRef.current) {
            clearInterval(elapsedTimeIntervalRef.current);
        }
        setElapsedTime(0); // Reseta o tempo decorrido
    };

    const openAlertCancelarMarcaVocal = () => {
        setAlertCancelarMarcaVocal(true);
        onClose();
    };

    useEffect(() => {
        // Limpar o timeout e o intervalo quando o componente for desmontado
        return () => {
            if (recordingTimeoutRef.current) {
                clearTimeout(recordingTimeoutRef.current);
            }
            if (elapsedTimeIntervalRef.current) {
                clearInterval(elapsedTimeIntervalRef.current);
            }
        };
    }, []);

    return (
        <>
            <Dialog
                open={open}
                onClose={() => {}} // Impede o fechamento ao clicar fora ou pressionar ESC
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown={true}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        padding: '10px',
                    },
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>Registro de Marca Vocal</DialogTitle>

                <DialogContent>
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" padding={2}>
                        {!isRecording && !recordingComplete && !isProcessing && (
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    backgroundColor: alpha(theme.palette.info.light, 0.15),
                                    borderRadius: '8px',
                                    borderLeft: `5px solid ${theme.palette.info.main}`,
                                }}
                            >
                                <Typography variant="body1" textAlign="center" paragraph sx={{ mb: 0, fontWeight: 'medium' }}>
                                    {message}
                                </Typography>
                            </Paper>
                        )}

                        {(recordingComplete || isProcessing) && (
                            <Typography variant="body1" textAlign="center" paragraph>
                                {message}
                            </Typography>
                        )}

                        {isRecording && (
                            <Typography variant="body1" textAlign="center" paragraph color="primary">
                                Por favor, leia o texto abaixo com voz clara e em volume adequado.
                            </Typography>
                        )}

                        {isRecording && (
                            <Box sx={{ textAlign: 'center', my: 1 }}>
                                <Typography variant="h6" color="text.secondary">
                                    {formatTime(elapsedTime)} / {formatTime(RECORDING_TIME_LIMIT_SECONDS)}
                                </Typography>
                            </Box>
                        )}

                        {isRecording && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    mb: 3,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    borderRadius: '8px',
                                    width: '100%',
                                    borderLeft: `5px solid ${theme.palette.primary.main}`,
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: theme.palette.primary.main,
                                    }}
                                >
                                    {textoParaLeitura}
                                </Typography>
                            </Paper>
                        )}

                        {isRecording ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', mb: 2 }}>
                                {/* Usar o componente AudioVisualizer */}
                                {audioStream && (
                                    <AudioVisualizer
                                        stream={audioStream}
                                        barColor={theme.palette.primary.main}
                                        height={50}
                                        barCount={20}
                                        barWidth={10}
                                        gap={3}
                                        style={{ marginTop: '16px' }}
                                    />
                                )}
                            </Box>
                        ) : recordingComplete ? (
                            <CheckCircleOutlineIcon sx={{ fontSize: 60, color: theme.palette.success.main, mb: 2 }} />
                        ) : isProcessing ? (
                            <Box sx={{ textAlign: 'center', my: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Processando áudio...
                                </Typography>
                            </Box>
                        ) : (
                            <MicIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    {!isRecording && !recordingComplete && !isProcessing && (
                        <Button variant="outlined" color="primary" onClick={openAlertCancelarMarcaVocal} size="large">
                            Sair e Não Registrar
                        </Button>
                    )}

                    {isRecording && (
                        <Button variant="contained" color="error" onClick={stopRecording} startIcon={<StopIcon />} size="large">
                            Parar Gravação
                        </Button>
                    )}

                    {recordingComplete && (
                        <>
                            <Button variant="outlined" onClick={resetRecording} size="large">
                                Registrar Novamente
                            </Button>
                            <Button variant="contained" color="primary" onClick={handleClose} size="large" sx={{ ml: 2 }}>
                                Concluir
                            </Button>
                        </>
                    )}

                    {!isRecording && !recordingComplete && !isProcessing && (
                        <Button variant="contained" color="primary" onClick={startRecording} startIcon={<MicIcon />} size="large">
                            Registrar Marca Vocal
                        </Button>
                    )}

                    {isProcessing && (
                        <Button variant="outlined" disabled size="large">
                            Processando...
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <Dialog
                open={alertCancelarMarcaVocal}
                slots={{
                    transition: Transition,
                }}
                keepMounted
                aria-describedby="alert-dialog-cancelar-marca-vocal"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    {'Tem certeza que não gostaria de gravar sua marca vocal?'}
                </DialogTitle>
                <DialogContent>
                    <Box display={'flex'} flexDirection="column" alignItems="center" justifyContent="center" mb={2}>
                        <WarningAmber sx={{ fontSize: 60 }} />
                    </Box>
                    <DialogContentText id="alert-dialog-cancelar-marca-vocal">
                        <p>
                            Registrar sua marca vocal permite que o sistema reconheça melhor sua voz, resultando em resumos mais
                            precisos e personalizados. Caso não registre, a qualidade dos resultados pode ser reduzida.
                        </p>
                        <p>Após feita, a marca vocal só será solicitada novamente após 24 horas.</p>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-evenly' }}>
                    <Button onClick={handleGiveUp} color="error" variant="outlined" sx={{ textTransform: 'capitalize' }}>
                        Não quero gravar
                    </Button>
                    <Button
                        onClick={() => {
                            setAlertCancelarMarcaVocal(false);
                            onOpen();
                        }}
                        variant="contained"
                    >
                        Ok, quero gravar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PopupMarcaVocal;
